import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Pricing from './Pricing';
import CallToAction from './CallToAction';

const ClickFunnels = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>ClickFunnels Video Integration - Sales Intercept</title>
        <meta name="description" content="Supercharge your ClickFunnels pages with video widgets. Boost conversions, reduce bounce rates, and engage visitors with personalized video content. Easy to integrate." />
      </Helmet>
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 lg:pt-40 pb-12 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6">
                Supercharge Your <span className="text-purple-si">ClickFunnels</span> with Video
              </h1>
              <p className="text-xl text-gray-700 mb-8">
                Add engaging video widgets to any ClickFunnels page in minutes. Boost conversions, 
                reduce bounce rates, and turn more visitors into customers with personalized video content.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://dashboard.salesintercept.com/signup" 
                   className="btn btn-primary">
                  Start Free Trial
                </a>
              </div>
              <p className="text-sm text-gray-500 italic mt-4">
                Works with any ClickFunnels page. No technical skills required.
              </p>
            </div>
            <div className="lg:w-1/2 relative">
              <div className="bg-white p-8 rounded-xl shadow-2xl">
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-4 bg-purple-si/10 rounded-lg">
                    <div className="text-3xl font-bold text-purple-si mb-2">47%</div>
                    <div className="text-sm">Higher Conversion Rates</div>
                  </div>
                  <div className="text-center p-4 bg-orange-dark-si/10 rounded-lg">
                    <div className="text-3xl font-bold text-orange-dark-si mb-2">3.2x</div>
                    <div className="text-sm">Longer Page Engagement</div>
                  </div>
                  <div className="text-center p-4 bg-aqua-si/10 rounded-lg">
                    <div className="text-3xl font-bold text-aqua-si mb-2">68%</div>
                    <div className="text-sm">Reduction in Bounce Rate</div>
                  </div>
                </div>
                <div className="text-center">
                  <p className="text-gray-600 font-medium">Results from ClickFunnels users</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ClickFunnels Integration Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Perfect for <span className="text-purple-si">ClickFunnels Users</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Whether you're running opt-in pages, sales pages, or webinar funnels, 
              Sales Intercept seamlessly integrates with your existing ClickFunnels setup.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">🎯</div>
              <h3 className="text-xl font-bold mb-2">Opt-in Pages</h3>
              <p className="text-gray-600">Add welcome videos that explain your lead magnet's value and increase opt-in rates by building instant trust.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">💰</div>
              <h3 className="text-xl font-bold mb-2">Sales Pages</h3>
              <p className="text-gray-600">Include product demos, testimonials, or founder stories that address objections and drive more sales.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">🎥</div>
              <h3 className="text-xl font-bold mb-2">Webinar Funnels</h3>
              <p className="text-gray-600">Tease your webinar content with preview videos and increase registration rates significantly.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">📱</div>
              <h3 className="text-xl font-bold mb-2">Mobile Optimized</h3>
              <p className="text-gray-600">Videos automatically adapt to mobile devices, ensuring your ClickFunnels pages perform well on all screens.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">⚡</div>
              <h3 className="text-xl font-bold mb-2">Fast Loading</h3>
              <p className="text-gray-600">Won't slow down your funnels. Videos load asynchronously and don't impact your page speed scores.</p>
            </div>
            
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="text-2xl mb-4">📊</div>
              <h3 className="text-xl font-bold mb-2">Analytics Integration</h3>
              <p className="text-gray-600">Track video engagement alongside your ClickFunnels analytics to optimize your entire funnel performance.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Add to Any ClickFunnels Page in <span className="text-orange-dark-si">3 Simple Steps</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-si text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold mb-2">Upload Your Video</h3>
                <p className="text-gray-600">Upload your video content to Sales Intercept dashboard. Use existing content from social media or record new videos.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-dark-si text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold mb-2">Copy the Code</h3>
                <p className="text-gray-600">Get your unique embed code from the dashboard. It's just one line of code, similar to adding Google Analytics.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-aqua-si text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold mb-2">Paste in ClickFunnels</h3>
                <p className="text-gray-600">Add the code to your ClickFunnels page header using the tracking code section. Your video widget goes live instantly.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for ClickFunnels Users */}
      <section className="py-16 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why ClickFunnels Users <span className="text-purple-si">Love Sales Intercept</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">No ClickFunnels Limitations</h3>
                    <p className="text-gray-600">Unlike ClickFunnels' built-in video features, Sales Intercept gives you complete control over video placement, timing, and interactions.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Works Across All Funnel Steps</h3>
                    <p className="text-gray-600">Add the same code to multiple pages in your funnel for consistent video engagement throughout the customer journey.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Repurpose Your Content</h3>
                    <p className="text-gray-600">Use the same videos you create for ads, social media, or webinars directly in your funnels without re-uploading.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Advanced Targeting</h3>
                    <p className="text-gray-600">Show different videos based on traffic source, time on page, or user behavior - perfect for segmented funnel traffic.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-si/10 to-aqua-si/10 p-8 rounded-xl">
              <h3 className="text-2xl font-bold mb-6 text-center">ClickFunnels Success Story</h3>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <img 
                    src="https://images.pexels.com/photos/3184328/pexels-photo-3184328.jpeg?auto=compress&cs=tinysrgb&w=100&h=100&dpr=2" 
                    alt="Sarah M." 
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-bold">Sarah M.</h4>
                    <p className="text-sm text-gray-600">Digital Marketing Agency</p>
                  </div>
                </div>
                <p className="text-gray-700 italic mb-4">
                  "We added Sales Intercept to our client's ClickFunnels opt-in page and saw a 52% increase in conversions within the first week. The personal video explaining the lead magnet made all the difference."
                </p>
                <div className="flex justify-between text-sm">
                  <span className="font-bold text-purple-si">+52% Conversions</span>
                  <span className="font-bold text-orange-dark-si">-34% Bounce Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Perfect for Every <span className="text-purple-si">Funnel Type</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl mb-4">🎯</div>
              <h3 className="font-bold mb-2">Lead Generation</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Welcome videos</li>
                <li>• Value explanations</li>
                <li>• Trust building</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl mb-4">💰</div>
              <h3 className="font-bold mb-2">Sales Funnels</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Product demos</li>
                <li>• Objection handling</li>
                <li>• Social proof</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl mb-4">🎓</div>
              <h3 className="font-bold mb-2">Course Sales</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Course previews</li>
                <li>• Instructor intros</li>
                <li>• Student testimonials</li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="text-3xl mb-4">🎪</div>
              <h3 className="font-bold mb-2">Event Funnels</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Event teasers</li>
                <li>• Speaker highlights</li>
                <li>• Urgency messaging</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Pricing />
      <CallToAction />
    </div>
    </>
  );
};

export default ClickFunnels;